<template>
  <div :class="['project-publishing', { 'project-publishing--has-reference-selector': showReferenceSelector }]">
    <div class="project-publishing__from row bottom between">
      <InputComponent
        :label="`${$t('publishingFrom')}?`"
        data-field="from"
        :disabled="Boolean(getDisabledField('publishing.from'))"
        v-tooltip.bottom="getDisabledField('publishing.from')"
      >
        <InputDate v-model="from" :disabled="Boolean(getDisabledField('publishing.from'))" />
      </InputComponent>
      <ButtonComponent
        @click="unpublish = true"
        :disabled="Boolean(getDisabledField('publishing.to'))"
        v-tooltip.bottom="getDisabledField('publishing.to')"
        :label="$t('unpublish')"
        :icon="['fal', 'xmark']"
      />
    </div>

    <Modal :show="unpublish" @close="close" clickaway class="project-publishing__unpublish">
      <Card tag="form" variant="prompt" :title="$t('unpublishWarningTitle')" @submit.prevent="unpublishSubmit">
        <div>
          {{ $t('unpublishWarning') }}
        </div>

        <template #actions>
          <ButtonComponent :label="$t('unpublishCancel')" @click="close" />
          <ButtonComponent
            :label="$t('unpublishConfirm')"
            theme="primary"
            :icon="['fal', 'check']"
            :promise="promise"
            type="submit"
          />
        </template>
      </Card>
    </Modal>

    <template v-if="showReferenceSelector">
      <YesNoSwitch
        class="project-publishing__yes-no-switch"
        :label="$t('publishProjectUnderPage')"
        v-model="showPageSelector"
        :popover-helper="{
          body: $t('publishProjectUnderPagePopover', {
            domain: `<a href='${regionDomain}'> ${regionDomain} </a>`,
            projectpath: `<a href='${`${regionDomain}/ankerhagen`}'> ${`${regionDomain}/ankerhagen`} </a>`,
          }),
        }"
      >
      </YesNoSwitch>
      <SelectComponent
        class="project-publishing__select-reference"
        v-model="publishRelation"
        v-if="showPageSelector"
        items="/api/resource/page?field.id=id&field.label=name&field.thumbnail=media.logo.url"
        search
        :label="$t('publishProjectSelectPage')"
      />
    </template>

    <InputComponent
      v-if="item"
      :class="['project-publishing__domain', { 'project-publishing__domain--show-dns': showDns }]"
      v-model.lazy="domain"
      :label="$t('domainOrWebAddress')"
      :placeholder="domainPlaceholder"
      prefix="https://"
      data-field="domain"
      :disabled="Boolean(getDisabledField('publishing.domain')) || showPageSelector"
      v-tooltip.bottom="getDisabledField('publishing.domain')"
      :is-valid="domainProps['is-valid']"
      :validation-message="domainProps['validation-message']"
    >
      <template v-if="!hasProjectReference" #after-label> <HelperPopover template="domain-helper" /> </template>
      <template #below>
        <DNS v-if="showDns" ref="dns" :domain="domain" class="mt-2" @update:dnsValidation="updateDomainProps" />
      </template>
    </InputComponent>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { InputComponent } from 'vue-elder-input'
import SelectComponent from '@kvass/custom-fields/src/Fields/SelectModal'
import { AlertComponent as Alert } from 'vue-elder-alert'
import DNS from '@/components/DNS/Component.vue'
import { DateTransformer, UrlTransformer } from '@/utils/transformers'
import YesNoSwitch from '@kvass/custom-fields/src/Fields/YesNoSwitch'
import { ModalComponent as Modal } from 'vue-elder-modal'
import Card from '@/components/Card-v2'
import { mergeDeepRight } from 'ramda'
import InputDate from '@/components/InputDate'
import moment from 'moment'
import Api from '@/api/Page'
import { Debounce } from '@/utils'
import HelperPopover from '@/components/HelperPopover/Component'

export default {
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    isSlot: Boolean,
    data: {
      type: Object,
      default: () => ({}),
    },
    validator: {
      type: Object,
      default: () => ({}),
    },
  },
  watch: {
    showDns: {
      handler(newVal) {
        if (!newVal) this.updateDomainProps(false)
      },
    },
    from(newValue, oldValue) {
      if (newValue) {
        this.to = null
      }
    },
    publishRelation: {
      handler(newValue, oldValue) {
        if (!this.showReferenceSelector) return
        if (newValue) {
          this.getRelation(newValue)
        }
      },
      immediate: true,
    },
    showPageSelector(newValue, oldValue) {
      if (!this.showReferenceSelector) return
      if (newValue && !this.publishRelation) {
        this.publishRelation = this.$path(['metadata', 'publishOnPage', 'suggestion'], this.item)
      }

      if (!newValue) {
        this.publishRelation = null
      }
      if (newValue) {
        this.value.domain = null
      }
    },
  },
  data() {
    return {
      showPageSelector: false,
      unpublish: false,
      promise: null,
      page: {},
      domainProps: {},
    }
  },
  computed: {
    ...mapState('Project', {
      item(state) {
        return state.item && Object.keys(state.item).length ? state.item : this.data
      },
    }),
    ...mapGetters('Project', { getDisabledFieldFromStore: 'getDisabledField' }),
    getDisabledField() {
      return field => {
        return this.getDisabledFieldFromStore(field, this.item)
      }
    },

    from: DateTransformer('value.from'),
    to: DateTransformer('value.to'),
    domain: UrlTransformer('value.domain'),
    redirectUrl: UrlTransformer('value.redirectUrl'),
    hasProjectReference() {
      return this.showReferenceSelector && this.$path(['relation', 'ref'], this.value)
    },
    domainPlaceholder() {
      if (!this.hasProjectReference) {
        return this.$t('domainPlaceholder')
      }

      return `${this.page.slug}/${this.page.projectPrefix ? `${this.page.projectPrefix}/` : ''}${this.$path(
        ['publishing', 'slug'],
        this.item,
      )}`
    },
    publishRelation: {
      get() {
        return this.$path(['relation', 'ref'], this.value)
      },
      set(newVal, oldVal) {
        this.value.relation = {
          ref: newVal,
          onModel: !newVal ? null : 'Page',
        }
      },
    },
    regionDomain() {
      return `www.${this.$store.state.domain}`
    },
    showReferenceSelector() {
      return (
        this.$path(['metadata', 'publishOnPage', 'hasPage'], this.item) &&
        !['tase', 'fjelge', 'livd'].includes(this.item.theme)
      )
    },
    showDns() {
      const hasDomain = Boolean(this.domain)
      const hasValidDomain = this.$path(['fields', 'publishing.domain', 'valid'], this.validator)
      return hasDomain && hasValidDomain
    },
  },
  methods: {
    ...mapActions('Project', ['mutate']),
    getRelation: Debounce(function (id) {
      return Api.relationMeta(id).then(res => {
        this.page = res
      })
    }, 200),
    unpublishSubmit() {
      const data = {
        publishing: {
          from: null,
          to: moment().subtract(1, 'd').format('yyyy-MM-DD'),
        },
      }

      return (this.promise = this.mutate([
        {
          id: this.item.id,
          data,
          getConfig: false,
        },
        {
          transformPayload: false,
          strategy: (item, payload) => {
            const { url, previewUrl, metadata, isPublished } = payload

            const merged = mergeDeepRight(item, {
              ...data,
              url,
              previewUrl,
              isPublished,
              metadata,
            })

            if (metadata && metadata.disabledFields) {
              merged.metadata.disabledFields = metadata.disabledFields
            }

            return merged
          },
          draft: false,
        },
      ]).then(() => {
        this.close()
      }))
    },
    close() {
      this.unpublish = false
    },
    updateDomainProps(value) {
      this.domainProps = this.getDomainProps(value)
    },
    getDomainProps(dnsIsValid) {
      if (this.validator.fields['publishing.domain'].valid === false)
        return {
          'is-valid': false,
          'validation-message': this.validator.fields['publishing.domain'].error,
        }

      if (!dnsIsValid) return { 'is-valid': undefined }
      return { 'is-valid': true }
    },
  },
  mounted() {
    if (this.publishRelation) {
      this.showPageSelector = true
    }
  },

  components: {
    InputComponent,
    Alert,
    SelectComponent,
    YesNoSwitch,
    DNS,
    Modal,
    Card,
    InputDate,
    HelperPopover,
  },
}
</script>

<style lang="scss">
.project-publishing {
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 1rem 2rem;
  &--has-reference-selector {
    grid-template-columns: repeat(2, 1fr);
    .project-publishing__domain,
    .project-publishing__select-reference {
      grid-column-end: span 2;
    }
    .project-publishing__select-reference {
      .field-select-modal__item-thumbnail {
        object-fit: contain;
      }
    }
  }

  &__from {
    gap: 1rem;
    [data-field='from'] {
      flex: 1;
    }
  }

  &__unpublish {
    .card-v2 {
      max-width: 600px;
    }
  }
  &__domain {
    &--show-dns {
      display: grid;
      grid-template-areas:
        'label label label label label'
        'input input input input dns-retry'
        'validation validation validation . .'
        'instructions instructions instructions instructions instructions';
      gap: 0.3rem 1rem;

      & > .elder-input__label {
        grid-area: label;
      }
      & > .elder-input__wrapper {
        grid-area: input;
        margin: 0;
      }
      & > .elder-input__validation-message {
        grid-area: validation;
      }
    }

    .dns-instructions {
      // for using first flex parent and display loading button beside domain input
      display: contents;

      &__retry {
        grid-area: dns-retry;
      }
      &__below {
        margin-top: 1rem;
        grid-area: instructions;
      }
    }
  }
}
</style>
